import React, { useEffect, useState } from "react";
import {
  Button,
  EmailChips,
  Modal,
  fieldLabelClasses,
  useToast,
} from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { fetchClient } from "../../clients/services/clientsService";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { savePlanPdf } from "../planDocument";
import { formatPlanDateLong } from "../planMapping";
import { fetchMealPlanPdf } from "../services/mealPlansService";
import type { PlanRecord } from "../types/plans.types";

export interface SendPlanModalProps {
  plan: PlanRecord;
  onClose: () => void;
  /**
   * Given the addresses it is going to — sending it is the caller's to do.
   *
   * Awaited: the dialog stands with its Send button working while the call is
   * in flight, and stays open if it is refused, so the addresses that were
   * typed are still there to try again with. Closing it is the caller's too,
   * once the plan has actually gone.
   */
  onSend: (emails: string[]) => void | Promise<void>;
}

/**
 * What the plan is, and where it is about to go.
 *
 * The details are shown rather than asked for: everything bar the address is
 * already settled by the time this opens, and reading it back is the last
 * chance to notice the plan was written for the wrong client.
 */
const SendPlanModal: React.FC<SendPlanModalProps> = ({
  plan,
  onClose,
  onSend,
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const toast = useToast();
  const getToken = useTokenGetter();

  /**
   * The client's own address, filled in from their record.
   *
   * Fetched rather than carried on the plan: a plan knows who it is for and
   * what they are called, and nothing more about them. It arrives as a chip
   * like any other — removable, and joined by whoever else the plan is being
   * copied to — so the box is a starting point rather than an answer.
   *
   * A client with no address on record, or a lookup that fell over, simply
   * leaves it empty: an address that could not be filled in is a convenience
   * that was not there, not a failure worth a message. Anything already typed
   * wins, since the fetch may land after someone has started.
   */
  useEffect(() => {
    if (!plan.clientId) return;

    const controller = new AbortController();

    fetchClient(getToken, plan.clientId, controller.signal)
      .then((client) => {
        const email = client.email?.trim();
        if (controller.signal.aborted || !email) return;
        setEmails((current) => (current.length === 0 ? [email] : current));
      })
      .catch(() => {});

    return () => controller.abort();
  }, [getToken, plan.clientId]);

  /** What a failed request tells the user, in the words the API used. */
  const reportFailure = (what: string, reason: unknown) =>
    toast.error(
      what,
      reason instanceof ApiError
        ? reason.message
        : "We could not reach the server. Check your connection and try again."
    );

  /**
   * The plan as a file. The backend renders it, so this waits on a request
   * rather than building one — hence a button that can be in flight.
   */
  const handleDownload = async () => {
    setDownloading(true);
    try {
      savePlanPdf(plan, await fetchMealPlanPdf(getToken, plan.id));
    } catch (reason) {
      reportFailure("Could not download the plan", reason);
    } finally {
      setDownloading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await onSend(emails);
    } finally {
      // Whatever happened, the button is a button again. The dialog itself is
      // only closed by the caller, and only once the plan has gone.
      setSending(false);
    }
  };

  const details: Array<[label: string, value: string]> = [
    ["Client name", plan.clientName],
    ["Goal", plan.planType],
    ["Plan name", plan.name],
    ["Plan code", plan.code],
    ["Created date", formatPlanDateLong(plan.createdOn)],
  ];

  return (
    <Modal
      open
      title="Send plan to client"
      onClose={onClose}
      className="max-w-[526px]"
      footer={
        <>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="mr-auto rounded-md px-1 py-1 text-[14px] text-brand transition-colors hover:text-brand-dark hover:underline disabled:cursor-not-allowed disabled:text-ink-subtle disabled:no-underline"
          >
            {/* Said rather than spun: the label is the only thing in this
                corner of the dialog, so there is room to say what is
                happening. */}
            {downloading ? "Preparing…" : "Download"}
          </button>
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="md"
            // Nowhere to send it to is not a send.
            disabled={emails.length === 0}
            loading={sending}
            onClick={handleSend}
          >
            Send
          </Button>
        </>
      }
    >
      {/* Label, colon and value in three columns, so the values line up down
          the dialog however long the labels get. */}
      <dl className="grid grid-cols-[100px_10px_1fr] gap-y-3 text-[14px]">
        {details.map(([label, value]) => (
          <React.Fragment key={label}>
            <dt className="text-ink-muted">{label}</dt>
            <dd aria-hidden="true" className="text-ink-muted">
              :
            </dd>
            <dd className="text-ink">{value}</dd>
          </React.Fragment>
        ))}
      </dl>

      <div className="mt-5 border-t border-line pt-4">
        <p className={`${fieldLabelClasses} mb-1.5`}>Client email</p>
        <EmailChips emails={emails} onChange={setEmails} />
      </div>
    </Modal>
  );
};

export default SendPlanModal;
